import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Wheat, Radio, ShieldCheck, Mic, ArrowRight, LifeBuoy } from "lucide-react";
import Footer from "@/components/Footer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1511081692775-05d0f180a065?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxiYWtlcnklMjBjYWZlJTIwaW50ZXJpb3IlMjB3YXJtfGVufDB8fHx8MTc4NjkwMzQ3Mnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const navigate = useNavigate();
  const [needsInit, setNeedsInit] = useState(false);

  useEffect(() => {
    api.get("/auth/init-status").then(({ data }) => {
      setNeedsInit(data.needs_init);
      if (data.needs_init) navigate("/setup");
    }).catch(() => {});
  }, [navigate]);

  return (
    <div className="min-h-screen bg-brand-page">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-brand flex items-center justify-center">
            <Wheat className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-display text-2xl leading-none text-brand-ink">The Spread</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-muted mt-0.5">Panera Coaching</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/help">
            <Button variant="ghost" data-testid="home-help-link" className="rounded-md text-brand-ink hover:bg-brand-sidebar">
              <LifeBuoy className="w-4 h-4 mr-1.5" /> Help Desk
            </Button>
          </Link>
          <Link to="/register">
            <Button variant="outline" data-testid="home-register-link" className="rounded-md border-brand-line">Claim Account</Button>
          </Link>
          <Link to="/login">
            <Button data-testid="home-login-link" className="rounded-full brand-btn px-5">Log In</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-sidebar border border-brand-line text-xs font-semibold text-brand">
            <span className="h-2 w-2 rounded-full bg-brand animate-pulse" /> Private · Golisano & Nappi, Syracuse NY
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-brand-ink mt-6 leading-[1.02]">
            Coaching that rises with your crew.
          </h1>
          <p className="text-lg text-brand-muted mt-6 leading-relaxed max-w-xl">
            The Spread is Panera's private podcast and article platform for ongoing training. Hosted by
            <span className="text-brand-ink font-semibold"> Elias</span> on brand standards and
            <span className="text-brand-ink font-semibold"> Maya</span> on food safety &amp; compliance.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/login">
              <Button data-testid="hero-login-button" className="rounded-full brand-btn px-6 h-12 text-base">
                Log in to listen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" data-testid="hero-claim-button" className="rounded-md border-brand-line h-12 px-6 text-base">
                Claim your account
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute -inset-4 bg-brand-dough/20 rounded-[2rem] -rotate-2" />
          <img src={HERO_IMG} alt="Warm bakery cafe" className="relative rounded-[1.5rem] object-cover w-full h-[440px] border border-brand-line card-shadow" />
        </div>
      </section>

      {/* Hosts */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: Mic, title: "Elias", role: "Brand Standards Expert", desc: "Brand identity, guest experience, food presentation, warmth, and hospitality.", color: "bg-brand-dough/15" },
          { icon: ShieldCheck, title: "Maya", role: "Food Safety & Compliance", desc: "Holding temps, cross-contamination, sanitation, Ecolab audits, and ServSafe standards.", color: "bg-brand/10" },
          { icon: Radio, title: "Every shift", role: "Fresh episodes", desc: "Latest episode ready the moment you log in. Track what you've heard and pin favorites.", color: "bg-brand-terracotta/10" },
        ].map((c, i) => (
          <div key={i} className={`rounded-2xl border border-brand-line p-8 lift ${c.color}`}>
            <div className="h-11 w-11 rounded-xl bg-white border border-brand-line flex items-center justify-center">
              <c.icon className="w-5 h-5 text-brand" />
            </div>
            <h3 className="font-display text-2xl text-brand-ink mt-5">{c.title}</h3>
            <p className="text-xs uppercase tracking-widest text-brand mt-1">{c.role}</p>
            <p className="text-brand-muted mt-3 leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </section>
      <Footer />
    </div>
  );
}
